import { useShallow } from 'zustand/react/shallow'
import { useOfferStore, selectAvailableSupermarkets, selectCategoryCounts, selectHasUpcoming } from '@store/offerStore'
import { SUPERMARKETS, CATEGORIES, SORT_OPTIONS } from '@utils/constants'
import styles from './FilterBar.module.css'

/** @import { SupermarketId, SupermarketMeta, CategoryFilter } from '../types.js' */

function WeekToggle({ selectedWeek, onSelect }) {
  return (
    <div className={styles.weekToggle} role="tablist" aria-label="Weekkeuze">
      <button
        className={`${styles.weekTab} ${selectedWeek === 'current' ? styles.weekTabActive : ''}`}
        onClick={() => onSelect('current')}
        role="tab"
        aria-selected={selectedWeek === 'current'}
        type="button"
      >
        <span className={styles.weekTabIcon}>📅</span>
        Deze week
      </button>
      <button
        className={`${styles.weekTab} ${selectedWeek === 'upcoming' ? styles.weekTabActive : ''}`}
        onClick={() => onSelect('upcoming')}
        role="tab"
        aria-selected={selectedWeek === 'upcoming'}
        type="button"
      >
        <span className={styles.weekTabIcon}>🔮</span>
        Volgende week
      </button>
    </div>
  )
}

function SupermarketChip({ id, meta, isActive, onToggle }) {
  const activeStyle = isActive
    ? { borderColor: meta.color, color: meta.color, background: hexToRgba(meta.color, 0.12) }
    : undefined

  return (
    <button
      className={`${styles.componentSupermarketChip} ${isActive ? styles.chipActive : ''}`}
      onClick={() => onToggle(id)}
      type="button"
      style={activeStyle}
    >
      <span
        className={styles.chipDot}
        style={{ backgroundColor: meta.color }}
      />
      {meta.shortLabel}
    </button>
  )
}

function CategoryButton({ cat, isActive, onSelect, count }) {
  return (
    <button
      className={`${styles.componentCategoryButton} ${isActive ? styles.categoryBtnActive : ''}`}
      onClick={() => onSelect(cat.id)}
      type="button"
    >
      <span>{cat.icon}</span>
      {cat.label}
      <span style={{ opacity: 0.6, marginLeft: '2px' }}>
        ({count})
      </span>
    </button>
  )
}

export function FilterBar({ layoutClassName = undefined }) {
  const availableSupermarkets = useOfferStore(useShallow(selectAvailableSupermarkets))
  const categoryCounts = useOfferStore(useShallow(selectCategoryCounts))
  const hasUpcoming = useOfferStore(selectHasUpcoming)

  const filters = useOfferStore(useShallow(s => ({
    selectedSupermarkets: s.selectedSupermarkets,
    selectedCategory: s.selectedCategory,
    selectedWeek: s.selectedWeek,
    searchQuery: s.searchQuery,
    sortBy: s.sortBy,
    semanticSearching: s.semanticSearching,
  })))

  const actions = useOfferStore(useShallow(s => ({
    toggleSupermarket: s.toggleSupermarket,
    setSelectedCategory: s.setSelectedCategory,
    setSelectedWeek: s.setSelectedWeek,
    setSearchQuery: s.setSearchQuery,
    setSortBy: s.setSortBy,
  })))

  const searchIcon = filters.semanticSearching ? '⏳' : '🔍'

  return (
    <div className={`${styles.component} ${layoutClassName || ''}`}>
      {hasUpcoming && (
        <WeekToggle
          selectedWeek={filters.selectedWeek}
          onSelect={actions.setSelectedWeek}
        />
      )}
      <div className={styles.searchRow}>
        <div className={styles.searchInput}>
          <span className={styles.searchIcon}>{searchIcon}</span>
          <input
            id="search-offers"
            className={styles.searchField}
            type="text"
            placeholder="Zoek op product (bijv. banaan, broccoli)..."
            value={filters.searchQuery}
            onChange={(e) => actions.setSearchQuery(e.target.value)}
          />
        </div>
        <select
          id="sort-offers"
          className={styles.sortSelect}
          value={filters.sortBy}
          onChange={(e) => actions.setSortBy(e.target.value)}
        >
          {Object.values(SORT_OPTIONS).map((opt) => (
            <option className={styles.sortOption} key={opt.id} value={opt.id}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className={styles.filterRow}>
        <div className={styles.chipGroup}>
          {availableSupermarkets.map((id) => {
            const meta = SUPERMARKETS[id] ?? {
              id,
              shortLabel: id.charAt(0).toUpperCase() + id.slice(1),
              color: idToColor(id),
            }
            return (
              <SupermarketChip
                key={id}
                {...{ id, meta }}
                isActive={filters.selectedSupermarkets.has(id)}
                onToggle={actions.toggleSupermarket}
              />
            )
          })}
        </div>
        <span className={styles.divider} aria-hidden="true" />
        <div className={styles.categoryToggle}>
          {Object.values(CATEGORIES)
            .filter((cat) => cat.id === 'all' || (categoryCounts[cat.id] ?? 0) > 0)
            .map((cat) => (
            <CategoryButton
              key={cat.id}
              {...{ cat }}
              isActive={filters.selectedCategory === cat.id}
              onSelect={actions.setSelectedCategory}
              count={categoryCounts[cat.id] ?? 0}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function hexToRgba(color, alpha) {
  if (color.startsWith('#')) {
    const r = Number.parseInt(color.slice(1, 3), 16)
    const g = Number.parseInt(color.slice(3, 5), 16)
    const b = Number.parseInt(color.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
  return `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, transparent)`
}

function idToColor(id) {
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash)
  }
  const lightness = 30 + (Math.abs(hash) % 40)
  return `hsl(0, 0%, ${lightness}%)`
}
